import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { getAbbrev } from "../pages/Attendance/Muster";
import { STATUS_ORDER } from "../pages/Attendance/Muster";

// Create a type from the STATUS_ORDER array
export type SessionStatus = typeof STATUS_ORDER[number];

interface DayStatus {
  firstSession?: SessionStatus;
  secondSession?: SessionStatus;
}

interface EmployeeMusterRow {
  userId: string;
  employeeNo?: string;
  name?: string;
  records?: { day: number; status: SessionStatus | DayStatus }[];
}

const STATUS_STYLES: Record<string, string> = {
  Present: "FFDCF5DC",
  Absent: "FFF8D7DA",
  "Paid Leave": "FFD6ECFA",
  "Sick Leave": "FFFDE5C8",
  "Casual Leave": "FFE6E6FA",
  CompOff: "FFF0F0F0",
  "Loss of Pay": "FFF8E6FA",
  Holiday: "FFEFEFEF",
  Weekend: "FFF5F5F5",
};


export function exportToExcel(
  data: EmployeeMusterRow[],
  filename: string,
  sheetName: string = "Sheet1"
) {
  const workbook = XLSX.utils.book_new();
  const wsData: any[][] = [];

  const totalDays = Math.max(...data.map(r => r.records?.length || 0));
  
  // Update headers: Day 1, Day 2, ...
  const headers = ["Employee"];
  for (let i = 1; i <= totalDays; i++) headers.push(`Day ${i}`);
  headers.push("Present", "Absent", "Paid Leave", "Sick Leave", "Causual Leave", "Comp Off", "Holiday", "LOP","OFF");

  // Push header row with centered alignment
  wsData.push(
    headers.map(h => ({
      v: h,
      s: { alignment: { horizontal: "center", vertical: "center" }, font: { bold: true } }
    }))
  );

  data.forEach(row => {
    const rowArr: any[] = [];
    rowArr.push(`${row.name} (${row.employeeNo ?? row.userId})`);

    const counts: Record<string, number> = {
      Present: 0, Absent: 0, "Paid Leave": 0, "Sick Leave": 0,
      "Casual Leave": 0, CompOff: 0, Holiday: 0, "Loss of Pay": 0,Weekend: 0,
    };

    for (let i = 1; i <= totalDays; i++) {
      const rec = row.records?.find(r => r.day === i);

      let value = "";
      let color = STATUS_STYLES.Absent; // default

      if (rec?.status) {
        if (typeof rec.status === "string") {
          value = getAbbrev(rec.status);
          color = STATUS_STYLES[rec.status] ?? color;
          counts[rec.status]++;
        } else {
          // Dual session – use | instead of /
          const s1 = rec.status.firstSession ?? "Absent";
          const s2 = rec.status.secondSession ?? "Absent";
          value = `${getAbbrev(s1)}|${getAbbrev(s2)}`;
          color = STATUS_STYLES[s1]; // pick first session color
          counts[s1] += 0.5;
          counts[s2] += 0.5;
        }
      }

      rowArr.push({
        v: value,
        s: {
          fill: { fgColor: { rgb: color } },
          alignment: { horizontal: "center", vertical: "center" },
        },
      });
    }

    ["Present", "Absent", "Paid Leave", "Sick Leave", "Casual Leave", "CompOff", "Holiday", "Loss of Pay", "Weekend"].forEach(s => {
      rowArr.push({ v: counts[s], s: { alignment: { horizontal: "center" } } });
    });

    wsData.push(rowArr);
  });

  const ws = XLSX.utils.aoa_to_sheet(wsData);
  XLSX.utils.book_append_sheet(workbook, ws, sheetName);
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(blob, `${filename}.xlsx`);
}
